import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  CircularProgress,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { 
  Visibility as VisibilityIcon, 
  Search as SearchIcon, 
  MailOutline as MailIcon,
  Close as CloseIcon,
  AlternateEmail as EmailIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from "../../contexts/AuthContext";

// --- DESIGN SYSTEM PREMIUM CORRESPONDANT À TES COMPOSANTS ---
const cardStyle = {
  borderRadius: '14px',
  boxShadow: '0 4px 18px 0 rgba(0, 0, 0, 0.03), 0 1px 3px 0 rgba(0, 0, 0, 0.02)',
  border: '1px solid #eef2f6',
  background: '#ffffff',
  overflow: 'hidden'
};

const tableHeaderStyle = {
  backgroundColor: '#f8fafc',
  '& th': {
    color: '#64748b',
    fontWeight: 600,
    fontSize: '0.80rem',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    borderBottom: '1px solid #f1f5f9',
    py: 2
  }
};

const EmailsHistory = () => {
  const { user } = useAuth();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pagination & Filtrage
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // État de la Modal d'aperçu
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Configuration de l'environnement comme tes autres modules
  const apiUrl = process.env.REACT_APP_API_URL || 'https://api-plateforme.yanacom.fr'; 
  const token = localStorage.getItem("token");

  // --- CHARGEMENT DES DONNÉES (MÊME STRUCTURE QUE TON EXEMPLE) ---
  const fetchEmailsHistory = useCallback(async () => {
    try {
      setLoading(true);
      
      // Appel de ta route avec le Bearer token configuré
      const response = await axios.get(`${apiUrl}/admin/v1/email/emails-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Injection de la liste selon ton format JSON : response.data.data.data
      if (response.data && response.data.status === 'success') {
        setEmails(response.data.data.data || []);
      }
    } catch (err) {
      console.error("Erreur API:", err);
      // toast.error("Erreur lors du chargement de l'historique des e-mails");
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token]);

  // Déclenchement automatique dès que le User est prêt
  useEffect(() => {
    if (user) fetchEmailsHistory();
  }, [user, fetchEmailsHistory]);

  // --- HANDLERS ---
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenPreview = (email) => {
    setSelectedEmail(email);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEmail(null);
  };

  // Filtrage combiné en temps réel
  const filteredEmails = emails.filter((email) => {
    const matchesSearch = 
      (email.recipient_email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (email.subject?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || email.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Badge de statut épuré style Apple
  const getStatusChip = (status) => {
    const isConfirmed = status === 'CONFIRMED';
    return (
      <Chip 
        label={isConfirmed ? "Envoyé" : status} 
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: '0.75rem',
          backgroundColor: isConfirmed ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          color: isConfirmed ? '#10b981' : '#ef4444',
          borderRadius: '8px',
          px: 0.5
        }}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress size={32} sx={{ color: '#10b981' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#fafafa', minHeight: '100vh' }}>
      
      {/* HEADER DE LA PAGE */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', letterSpacing: '-0.5px', mb: 0.5 }}>
            Historique des e-mails
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem' }}>
            Consultez et inspectez les e-mails transactionnels émis par l'application.
          </Typography>
        </Box>
      </Stack>

      {/* ZONE DE RECHERCHE ET FILTRES */}
      <Card sx={{ ...cardStyle, p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher par e-mail ou objet du message..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fdfdfd' } }}
          />
          <TextField
            select
            size="small"
            label="État de distribution"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 190, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          >
            <MenuItem value="ALL">Tous les e-mails</MenuItem>
            <MenuItem value="CONFIRMED">Confirmés / Envoyés</MenuItem>
            <MenuItem value="PENDING">En attente de traitement</MenuItem>
          </TextField>
        </Stack>
      </Card>

      {/* TABLEAU DE DONNÉES */}
      <Card sx={cardStyle}>
        <TableContainer>
          <Table>
            <TableHead sx={tableHeaderStyle}>
              <TableRow>
                <TableCell width="80">ID</TableCell>
                <TableCell width="240">Destinataire</TableCell>
                <TableCell>Objet du message</TableCell>
                <TableCell width="180">Date d'expédition</TableCell>
                <TableCell width="120">Statut</TableCell>
                <TableCell width="80" align="center">Aperçu</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmails
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((email) => (
                  <TableRow 
                    key={email.id_history}
                    sx={{ '&:hover': { backgroundColor: '#f8fafc/50' }, '& td': { borderBottom: '1px solid #f8fafc', py: 1.8 } }}
                  >
                    <TableCell sx={{ fontWeight: 500, color: '#94a3b8', fontSize: '0.85rem' }}>
                      #{email.id_history}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <EmailIcon sx={{ fontSize: 16, color: '#cbd5e1' }} />
                        <span>{email.recipient_email}</span>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ color: '#334155', fontWeight: 500, fontSize: '0.875rem', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {email.subject}
                    </TableCell>
                    <TableCell sx={{ color: '#64748b', fontSize: '0.85rem' }}>
                      {email.sent_at}
                    </TableCell>
                    <TableCell>
                      {getStatusChip(email.status)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Inspecter le contenu HTML" arrow>
                        <IconButton 
                          onClick={() => handleOpenPreview(email)}
                          sx={{ 
                            color: '#64748b', 
                            backgroundColor: '#f8fafc',
                            border: '1px solid #eef2f6',
                            '&:hover': { backgroundColor: '#f1f5f9', color: '#0f172a' },
                            borderRadius: '8px',
                            p: 0.8
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              
              {filteredEmails.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                    <MailIcon sx={{ fontSize: 44, color: '#cbd5e1', mb: 1.5 }} />
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                      Aucune correspondance enregistrée dans l'historique.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={filteredEmails.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page:"
          sx={{ borderTop: '1px solid #f1f5f9' }}
        />
      </Card>

      {/* --- DIALOG DE PRÉVISUALISATION --- */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: { borderRadius: '16px', boxShadow: '0 24px 48px -12px rgba(0,0,0,0.12)' }
        }}
      >
        {selectedEmail && (
          <>
            <DialogTitle sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fafafa' }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                  {selectedEmail.subject}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                  Destinataire : <strong>{selectedEmail.recipient_email}</strong> • Envoyé le {selectedEmail.sent_at}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseModal} sx={{ borderRadius: '8px', backgroundColor: '#ffffff', border: '1px solid #eef2f6', '&:hover': { backgroundColor: '#f1f5f9' } }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </DialogTitle>
            
            <Divider />

            <DialogContent sx={{ p: 2, backgroundColor: '#f4f5f6', minHeight: '480px', display: 'flex' }}>
              <Box 
                sx={{ 
                  width: '100%', 
                  backgroundColor: '#ffffff', 
                  borderRadius: '10px', 
                  overflow: 'hidden',
                  border: '1px solid #eef2f6',
                  display: 'block'
                }}
              >
                <iframe
                  title={`Visualisation de l'email ${selectedEmail.id_history}`}
                  srcDoc={selectedEmail.body_html}
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '460px',
                    border: 'none',
                    display: 'block'
                  }}
                />
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

    </Box>
  );
};

export default EmailsHistory;